import React, { useEffect, useMemo, useState } from "react";

import DashboardLayout from "components/layouts/dashboard-layout";
import { DiscographyDummyData } from "constants/dummy-data/discography-dummy-data";
import PageHeader from "./components/header";
import ListView from "./components/list-view";
import BlockView from "./components/block-view";
import styled from "styled-components";
import { Colors } from "utils/colors";
import Typography from "components/typography";
import DateConvert from "utils/dateConvert";
import PageLinks from "components/page-links";
import { ITrackRelease } from "types/musicData";
import { GetActiveArtist } from "services/user/getUser";
import api from "services/axiosInstance";
import { SongWithUrls } from "types/response-type";
import { ReleaseContainerWithUrls } from "types/response-type";
import Toast from "components/toast";
import Spinner from "components/spinner";
import { isTrackRelease } from "../../components/track-list-item";

export interface IFilter {
  filter: [
    { section: "Date"; filter: { to: Date | null; from: Date | null } },
    { section: "Artist"; filter: string },
    { section: "Status"; filter: string },
    { section: "Type"; filter: string }
  ];
}
const DashboardOverviewDiscography = () => {
  const [viewType, setViewType] = useState("list");
  const [searchInput, setSearchInput] = useState("");
  const [searchFilter, setSearchFilter] = useState<IFilter>({
    filter: [
      { section: "Date", filter: { to: null, from: null } },
      { section: "Artist", filter: "" },
      { section: "Status", filter: "" },
      { section: "Type", filter: "" }
    ]
  });
  const resetFilterItem = (sectionToReset: string) => {
    setSearchFilter({
      filter: [
        sectionToReset === "Date"
          ? { section: "Date", filter: { from: null, to: null } }
          : searchFilter.filter[0], // Date filter

        sectionToReset === "Artist"
          ? { section: "Artist", filter: "" }
          : searchFilter.filter[1], // Artist filter

        sectionToReset === "Status"
          ? { section: "Status", filter: "" }
          : searchFilter.filter[2], // Status filter

        sectionToReset === "Type"
          ? { section: "Type", filter: "" }
          : searchFilter.filter[3] // Type filter
      ] as [
        { section: "Date"; filter: { from: Date | null; to: Date | null } },
        { section: "Artist"; filter: string },
        { section: "Status"; filter: string },
        { section: "Type"; filter: string }
      ]
    });
  };

  const [songs, setSongs] = useState<
    (SongWithUrls | ReleaseContainerWithUrls)[]
  >([]);
  const artist = GetActiveArtist();
  const [loading, setLoading] = useState(false);

  const GetDiscography = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/songs/artist/${artist?.id}/discography`);
      const { singles, albums, eps } = data.data;
      const singleData: SongWithUrls[] = singles;
      const albumData: ReleaseContainerWithUrls[] = albums;
      const epData: ReleaseContainerWithUrls[] = eps;
      const discography = [...singleData, ...albumData, ...epData];

      setSongs(discography);
    } catch (error) {
      Toast({ error });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    GetDiscography();
  }, []);

  const FilteredSongs = useMemo(() => {
    let filteredSongs = songs;

    //search
    if (searchInput.length > 0) {
      filteredSongs = filteredSongs.filter((song) => {
        return song.title.toLowerCase().includes(searchInput.toLowerCase());
      });
    }
    //date
    if (
      searchFilter.filter[0].filter.from &&
      searchFilter.filter[0].filter.to
    ) {
      if (searchFilter.filter[0].filter.from) {
        filteredSongs = filteredSongs.filter((song) => {
          if (isTrackRelease(song)) {
            let track = song as SongWithUrls;
            return (
              new Date(track.proposedReleaseDate) >=
              new Date(searchFilter.filter[0].filter.from || "")
            );
          } else {
            let track = song as ReleaseContainerWithUrls;
            return (
              new Date(track.releaseDate) >=
              new Date(searchFilter.filter[0].filter.from || "")
            );
          }
        });
      }
      if (searchFilter.filter[0].filter.to) {
        filteredSongs = filteredSongs.filter((song) => {
          if (isTrackRelease(song)) {
            let track = song as SongWithUrls;
            return (
              new Date(track.proposedReleaseDate) <=
              new Date(searchFilter.filter[0].filter.to || "")
            );
          } else {
            let track = song as ReleaseContainerWithUrls;
            return (
              new Date(track.releaseDate) <=
              new Date(searchFilter.filter[0].filter.to || "")
            );
          }
        });
      }
    }
    //artist
    if (searchFilter.filter[1].filter) {
      filteredSongs = filteredSongs.filter((song) => {
        if (isTrackRelease(song)) {
          let track = song as SongWithUrls;
          return track.primaryArtist.name
            .toLowerCase()
            .includes(searchFilter.filter[1].filter.toLowerCase());
        } else {
          let track = song as ReleaseContainerWithUrls;
          return track.tracks[0].primaryArtist.name
            .toLowerCase()
            .includes(searchFilter.filter[1].filter.toLowerCase());
        }
      });
    }
    //status
    if (searchFilter.filter[2].filter) {
      filteredSongs = filteredSongs.filter((song) => {
        return song.status
          .toLowerCase()
          .includes(searchFilter.filter[2].filter.toLowerCase());
      });
    }
    //type

    if (searchFilter.filter[3].filter) {
      filteredSongs = filteredSongs.filter((song) => {
        if (isTrackRelease(song)) {
          let track = song as SongWithUrls;
          return track.releaseType
            .toLowerCase()
            .includes(searchFilter.filter[3].filter.toLowerCase());
        } else {
          let track = song as ReleaseContainerWithUrls;
          return track.type
            .toLowerCase()
            .includes(searchFilter.filter[3].filter.toLowerCase());
        }
      });
    }

    return filteredSongs;
  }, [songs, searchFilter, searchInput]);

  return (
    <DashboardLayout>
      <>
        <PageLinks
          from={{ name: "Overview", link: "/overview" }}
          current="Discography"
        />
        <PageHeader
          onChangeView={(viewType) => setViewType(viewType)}
          onChangeSearch={(value) => setSearchInput(value)}
          filter={searchFilter}
          setFilter={(value) => setSearchFilter(value)}
        />
        {searchFilter.filter.length > 0 && (
          <Filters>
            {searchFilter.filter.map(({ section, filter }, i) => {
              const to = searchFilter.filter[0].filter.to;
              const from = searchFilter.filter[0].filter.from;

              if (filter === "") {
                return null; // Skip empty filter items.
              }

              if (i === 0) {
                if (to === null || from === null) {
                  return null; // Skip empty date filter.
                }
                return (
                  <FilterItem key={i}>
                    <Typography color={Colors.grey400} size="s">
                      {section}:
                    </Typography>
                    <Typography
                      color={Colors.grey400}
                      size="s"
                      ml="0.4"
                      weight="semibold"
                    >
                      {DateConvert(from)} - {DateConvert(to)}
                    </Typography>
                    <CloseIcon
                      onClick={() => {
                        setSearchFilter({
                          filter: [
                            {
                              section: "Date",
                              filter: { to: null, from: null }
                            },
                            searchFilter.filter[1],
                            searchFilter.filter[2],
                            searchFilter.filter[3]
                          ]
                        });
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                      >
                        <path
                          d="M9 3L3 9.00001M3 3L9 9.00001"
                          stroke="#CDCDCD"
                          stroke-width="0.642857"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </CloseIcon>
                  </FilterItem>
                );
              }

              return (
                <FilterItem key={i} onClick={() => resetFilterItem(section)}>
                  <Typography color={Colors.grey400} size="s">
                    {section}:
                  </Typography>
                  <Typography
                    color={Colors.grey400}
                    size="s"
                    ml="0.4"
                    weight="semibold"
                  >
                    {filter}
                  </Typography>
                  <CloseIcon>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                    >
                      <path
                        d="M9 3L3 9.00001M3 3L9 9.00001"
                        stroke="#CDCDCD"
                        stroke-width="0.642857"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </CloseIcon>
                </FilterItem>
              );
            })}
          </Filters>
        )}
        {loading ? (
          <LoadingContainer>
            <Spinner />{" "}
          </LoadingContainer>
        ) : (
          <>
            {viewType === "list" && <ListView data={FilteredSongs} />}
            {viewType === "block" && <BlockView data={FilteredSongs} />}
          </>
        )}
      </>
    </DashboardLayout>
  );
};

export default DashboardOverviewDiscography;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 30rem;
`;

const Filters = styled.div`
  display: flex;
  align-items: center;
  justify-content: end;
  margin-bottom: 1rem;
`;

const FilterItem = styled.div`
  border-radius: 0.4rem;

  background: ${Colors.grey600};
  padding: 0.4rem 0.8rem;
  margin-left: 0.8rem;
  margin-top: 1.2rem;
  display: flex;
`;

const CloseIcon = styled.div`
  width: 1.2rem;
  cursor: pointer;
  margin-left: 0.4rem;
`;
